import { supabase } from '../../integrations/supabase/client';
import type {
  IRoleTemplateRepository,
  RoleTemplate,
  OrgTypeRoleRequirement,
  RoleOnboardingConfig,
} from '../interfaces/IRoleTemplateRepository';

export class SupabaseRoleTemplateRepository implements IRoleTemplateRepository {
  async getAll(): Promise<RoleTemplate[]> {
    const { data, error } = await supabase
      .from('role_templates')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (error) {
      console.error('Error fetching role templates:', error);
      throw error;
    }

    return (data || []).map(template => ({
      ...template,
      context_type: template.context_type || 'client',
      onboarding_steps: template.onboarding_steps as any,
      field_requirements: template.field_requirements as any,
      validation_rules: template.validation_rules as any,
      default_permissions: template.default_permissions as any,
    })) as RoleTemplate[];
  }

  async getAllByContext(context: 'client' | 'supplier' | 'partner'): Promise<RoleTemplate[]> {
    const { data, error } = await supabase
      .from('role_templates')
      .select('*')
      .eq('is_active', true)
      .eq('context_type', context)
      .order('display_order');

    if (error) {
      console.error(`Error fetching ${context} role templates:`, error);
      throw error;
    }

    return (data || []).map(template => ({
      ...template,
      context_type: template.context_type || 'client',
      onboarding_steps: template.onboarding_steps as any,
      field_requirements: template.field_requirements as any,
      validation_rules: template.validation_rules as any,
      default_permissions: template.default_permissions as any,
    })) as RoleTemplate[];
  }

  async getById(id: string): Promise<RoleTemplate | null> {
    const { data, error } = await supabase
      .from('role_templates')
      .select('*')
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching role template by ID:', error);
      throw error;
    }

    if (!data) return null;

    return {
      ...data,
      context_type: data.context_type || 'client',
      onboarding_steps: data.onboarding_steps as any,
      field_requirements: data.field_requirements as any,
      validation_rules: data.validation_rules as any,
      default_permissions: data.default_permissions as any,
    } as RoleTemplate;
  }

  async getByCode(roleCode: string): Promise<RoleTemplate | null> {
    const { data, error } = await supabase
      .from('role_templates')
      .select('*')
      .eq('role_code', roleCode)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching role template by code:', error);
      throw error;
    }

    if (!data) return null;

    return {
      ...data,
      context_type: data.context_type || 'client',
      onboarding_steps: data.onboarding_steps as any,
      field_requirements: data.field_requirements as any,
      validation_rules: data.validation_rules as any,
      default_permissions: data.default_permissions as any,
    } as RoleTemplate;
  }

  async getRequiredRolesForOrgType(orgTypeId: string): Promise<OrgTypeRoleRequirement[]> {
    const { data, error } = await supabase
      .rpc('get_required_roles_for_org_type', {
        _org_type_id: orgTypeId,
      });

    if (error) {
      console.error('Error fetching required roles for org type:', error);
      throw error;
    }

    return (data || []) as OrgTypeRoleRequirement[];
  }

  async getRoleOnboardingConfig(
    roleId: string,
    platformTenantId?: string
  ): Promise<RoleOnboardingConfig | null> {
    const { data, error } = await supabase
      .rpc('get_role_onboarding_config', {
        _role_id: roleId,
        _platform_tenant_id: platformTenantId || undefined,
      });

    if (error) {
      console.error('Error fetching role onboarding config:', error);
      throw error;
    }

    return data as RoleOnboardingConfig | null;
  }

  async isFieldRequiredForRole(
    roleId: string,
    orgTypeId: string,
    fieldName: string
  ): Promise<'required' | 'optional' | 'conditional'> {
    const { data, error } = await supabase
      .rpc('is_field_required_for_role', {
        _role_id: roleId,
        _org_type_id: orgTypeId,
        _field_name: fieldName,
      });

    if (error) {
      console.error('Error checking field requirement:', error);
      throw error;
    }

    return data as 'required' | 'optional' | 'conditional';
  }
}
